import React from 'react';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';
import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';

import { blockHeading } from '../panelHeading';
import { STATUS_PILL_HUES, statusPill, statusPillLabel, StatusPillTone } from '../statusPill';

export type EventTimelineTone = StatusPillTone;

/**
 * How the `status` column draws its word. `pill` is the tinted pill 3583-10820 gives the reversal
 * timeline; `text` is the bare toned word 3583-7541 draws in Restriction History, which carries the
 * tone and none of the pill's fill, padding or radius. PLA-8828.
 */
export type EventTimelineStatusVariant = 'pill' | 'text';

/**
 * The columns `3583-10820` draws, as roles rather than free-form headers: `Date/Time`, `Type`,
 * `Reward/Entitlement`, `Status`, `Points/Value`, `Source`, `Details`. A caller picks the ones its
 * card carries, so the reversal timeline and the engagement timeline share one table.
 */
export type EventTimelineField =
  | 'timestamp'
  | 'type'
  | 'subject'
  | 'status'
  | 'amount'
  | 'source'
  | 'detail';

export interface IEventTimelineColumn {
  field: EventTimelineField;
  /** Already translated. Omitted from every column, the table draws no header row. */
  label?: string;
}

export interface IEventTimelineEvent {
  id: string;
  /** Pre-formatted by the caller — `10 May, 04:45 PM`. */
  timestamp: string;
  /** What happened — `Redeemed`, `Issued`, `Reversal`. */
  type: string;
  status?: { label: string; tone?: EventTimelineTone };
  /** What it happened to — `Amazon Pay Voucher`. */
  subject?: string;
  /** Its reading — `750`, `+1,200 pts`. */
  amount?: string;
  source?: string;
  /** The outcome in words — `Provider cancellation · REV-2025-76311`. */
  detail?: string;
  /** Renders the event's detail cell as a button; omitted, the row is read-only text. */
  onSelect?: () => void;
}

export interface IEventTimelineProps {
  /** The block heading — `Recent Entitlement & Reversal Timeline`. */
  title: string;
  events: IEventTimelineEvent[];
  columns?: IEventTimelineColumn[];
  /** Shown in place of the table when nothing has happened in the period. */
  emptyText?: string;
  /** Slot for the trailing affordance — the `View More` link the frame ends on. */
  footer?: React.ReactNode;
  /** Defaults to `pill`, so a timeline that does not ask for the bare word keeps the pill. */
  statusVariant?: EventTimelineStatusVariant;
  className?: string;
}

const DEFAULT_TONE: EventTimelineTone = 'neutral';

const DEFAULT_COLUMNS: IEventTimelineColumn[] = [
  { field: 'timestamp' },
  { field: 'type' },
  { field: 'status' }
];

/* PLA-8844: the table below is table-layout auto with width 100%, and a percentage width is a floor
   for such a table rather than a cap - it cannot draw narrower than its own min-content, which is
   630.6px on the C360 Rewards & Loyalty timeline against a 530px section. It therefore painted over
   the card beside it. Scrolling the panel is the fix that leaves the drawn table alone: it engages
   only where the table does not fit, so nothing changes at any width where it already did. The two
   alternatives were both worse - table-layout fixed redistributes every column at EVERY width,
   including with the flag off, and there are no fixed column widths here to scale, the cells being
   padded in scaled tokens already. min-width 0 is what lets the panel shrink when it is a flex or
   grid child, which is the state the section puts it in. */
const Panel = styled.section`
  min-width: 0;
  padding: ${tokens.space['16']};
  border: 1px solid ${tokens.color['border-subtle']};
  border-radius: ${tokens.radius.md};
  background: ${tokens.color['surface-section']};
  overflow-x: auto;

  ${intelligenceScrollbar('x')}
`;

const Title = styled.h4`
  ${blockHeading}

  /* 3583-10820 heads this block in sentence case, not the uppercase every other in-card block
     label carries. */
  && {
    color: ${tokens.color['text-primary']};
    font-size: ${tokens.type['whybox-heading-size']};
    font-weight: ${tokens.type['card-title-weight']};
    line-height: ${tokens.type['whybox-heading-line']};
    letter-spacing: normal;
    text-transform: none;
  }
`;

const Table = styled.table`
  width: 100%;
  margin-top: ${tokens.space['12']};
  border-collapse: collapse;
  table-layout: auto;
`;

const HeaderCell = styled.th`
  ${blockHeading}

  padding: 0 ${tokens.space['12']} ${tokens.space['8']} 0;
  text-align: left;
  vertical-align: bottom;

  &:last-child {
    padding-right: 0;
  }
`;

const BodyRow = styled.tr`
  border-top: 1px solid ${tokens.color['border-subtle']};
`;

const BodyCell = styled.td`
  padding: ${tokens.space['8']} ${tokens.space['12']} ${tokens.space['8']} 0;
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
  text-align: left;
  vertical-align: middle;

  &:last-child {
    padding-right: 0;
  }
`;

const Pill = styled.span<{ $hue: string }>`
  ${statusPill}
`;

const PillLabel = styled.span`
  ${statusPillLabel}
`;

// The bare toned word the State column of 3583-7541 draws: the pill's tone on the table's own body
// type, with no fill and no radius behind it. PLA-8828.
const StatusText = styled.span<{ $hue: string }>`
  color: ${({ $hue }) => $hue};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
  white-space: nowrap;
`;

const DetailButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${tokens.color['accent-blue']};
  cursor: pointer;
  font: inherit;
  text-align: left;

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

const EmptyText = styled.p`
  margin: ${tokens.space['12']} 0 0;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const Footer = styled.div`
  display: flex;
  margin-top: ${tokens.space['12']};
  justify-content: flex-end;
`;

const renderCell = (
  event: IEventTimelineEvent,
  field: EventTimelineField,
  statusVariant: EventTimelineStatusVariant
): React.ReactNode => {
  if (field === 'status') {
    if (!event.status) {
      return null;
    }

    const tone = event.status.tone || DEFAULT_TONE;

    if (statusVariant === 'text') {
      return (
        <StatusText
          $hue={STATUS_PILL_HUES[tone]}
          data-tone={tone}
          data-testid="event-timeline-status"
        >
          {event.status.label}
        </StatusText>
      );
    }

    // The pill's own word carries the outcome, so the tone hue only reinforces it and no reading
    // depends on colour (WCAG 1.4.1).
    return (
      <Pill $hue={STATUS_PILL_HUES[tone]} data-tone={tone} data-testid="event-timeline-status">
        <PillLabel>{event.status.label}</PillLabel>
      </Pill>
    );
  }

  if (field === 'detail' && event.onSelect) {
    return (
      <DetailButton type="button" onClick={event.onSelect} data-testid="event-timeline-detail">
        {event.detail}
      </DetailButton>
    );
  }

  return event[field];
};

const EventTimeline: React.FC<IEventTimelineProps> = ({
  title,
  events,
  columns = DEFAULT_COLUMNS,
  emptyText,
  footer,
  statusVariant = 'pill',
  className
}) => {
  const rows = events ?? [];
  const hasHeaderRow = columns.some(column => Boolean(column.label));

  // An empty period is a fact about the customer, not a degraded card, so it says so in place of the
  // table rather than leaving an empty grid behind.
  const empty = emptyText ? (
    <EmptyText data-testid="event-timeline-empty">{emptyText}</EmptyText>
  ) : null;

  return (
    <Panel className={className} aria-label={title} data-testid="event-timeline">
      <Title data-testid="event-timeline-title">{title}</Title>

      {rows.length === 0 ? (
        empty
      ) : (
        <Table>
          {hasHeaderRow ? (
            <thead>
              <tr>
                {columns.map(column => (
                  <HeaderCell key={column.field} scope="col">
                    {column.label}
                  </HeaderCell>
                ))}
              </tr>
            </thead>
          ) : null}

          <tbody>
            {rows.map(event => (
              <BodyRow key={event.id} data-testid="event-timeline-row">
                {columns.map((column, index) =>
                  // The timestamp heads its own row: a chronological list is ordered by it, so it is
                  // the cell that names the row rather than a cell the row names.
                  index === 0 && column.field === 'timestamp' ? (
                    <BodyCell key={column.field} as="th" scope="row">
                      {event.timestamp}
                    </BodyCell>
                  ) : (
                    <BodyCell key={column.field}>
                      {renderCell(event, column.field, statusVariant)}
                    </BodyCell>
                  )
                )}
              </BodyRow>
            ))}
          </tbody>
        </Table>
      )}

      {footer ? <Footer>{footer}</Footer> : null}
    </Panel>
  );
};

export default EventTimeline;
